/**
 * Get the computed value of a CSS variable
 */
export function getCSSVariable(variableName: string): string {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
  return value;
}

/**
 * Get email-specific CSS variable value
 * @param variableName - The name of the email variable (without '--color-email-' prefix)
 * @returns The CSS variable value
 */
export function getEmailCSSVariable(variableName: string): string {
  const fullVariableName = `--color-email-${variableName}`;
  return getCSSVariable(fullVariableName);
}

/**
 * Set a CSS variable value
 */
export function setCSSVariable(variableName: string, value: string): void {
  document.documentElement.style.setProperty(variableName, value);
}

/**
 * Get all theme-related CSS variables
 */
export function getAllThemeVariables(): Record<string, string> {
  const styles = getComputedStyle(document.documentElement);
  const variables: Record<string, string> = {};
  
  for (let i = 0; i < styles.length; i++) {
    const name = styles[i];
    if (name.startsWith('--color-') || 
        name.startsWith('--font-') || 
        name.startsWith('--spacing-') ||
        name.startsWith('--radius-') ||
        name.startsWith('--shadow-') ||
        name.startsWith('--tw-')) {
      variables[name] = styles.getPropertyValue(name).trim();
    }
  }
  
  return variables;
}

/**
 * Apply a theme object to CSS variables
 */
export function applyThemeVariables(theme: Record<string, string>): void {
  Object.entries(theme).forEach(([key, value]) => {
    setCSSVariable(key, value);
  });
}

/**
 * Reset all theme variables to defaults
 */
export function resetThemeVariables(): void {
  const root = document.documentElement;
  const styles = root.style;
  
  // Remove all custom properties
  for (let i = styles.length - 1; i >= 0; i--) {
    const prop = styles[i];
    if (prop.startsWith('--color-') || 
        prop.startsWith('--font-') ||
        prop.startsWith('--spacing-') ||
        prop.startsWith('--radius-') ||
        prop.startsWith('--shadow-')) {
      root.style.removeProperty(prop);
    }
  }
}

/**
 * Convert hex color to RGB
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Convert RGB to hex color
 */
export function rgbToHex(r: number, g: number, b: number): string {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/**
 * Check if a color meets WCAG contrast requirements
 */
export function checkContrast(foreground: string, background: string): {
  ratio: number;
  passesAA: boolean;
  passesAAA: boolean;
  passesLargeAA: boolean;
  passesLargeAAA: boolean;
} {
  const getLuminance = (color: string): number => {
    const rgb = hexToRgb(color);
    if (!rgb) return 0;
    
    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(val => {
      val = val / 255;
      return val <= 0.03928
        ? val / 12.92
        : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  
  const lum1 = getLuminance(foreground);
  const lum2 = getLuminance(background);
  
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  
  const ratio = (lighter + 0.05) / (darker + 0.05);
  
  return {
    ratio: Math.round(ratio * 100) / 100,
    passesAA: ratio >= 4.5,
    passesAAA: ratio >= 7,
    passesLargeAA: ratio >= 3,
    passesLargeAAA: ratio >= 4.5
  };
}

/**
 * Export theme as JSON
 */
export function exportThemeAsJSON(theme: Record<string, string>): string {
  return JSON.stringify(theme, null, 2);
}

/**
 * Import theme from JSON
 */
export function importThemeFromJSON(json: string): Record<string, string> {
  try {
    const theme = JSON.parse(json);
    if (typeof theme !== 'object' || theme === null) {
      throw new Error('Invalid theme format');
    }
    return theme;
  } catch (error) {
    throw new Error('Failed to parse theme JSON: ' + (error as Error).message);
  }
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    // Error: Failed to copy to clipboard:
    return false;
  }
}

/**
 * Generate a unique theme ID
 */
export function generateThemeId(): string {
  return `theme-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Adjust color lightness to meet contrast requirements
 */
export function adjustColorForContrast(
  foreground: string,
  background: string,
  targetRatio: number = 4.5
): string {
  const fgRgb = hexToRgb(foreground);
  const bgRgb = hexToRgb(background);
  
  if (!fgRgb || !bgRgb) return foreground;
  
  // Convert to HSL for easier lightness adjustment
  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;
    
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    
    return { h: h * 360, s: s * 100, l: l * 100 };
  };
  
  const hslToRgb = (h: number, s: number, l: number) => {
    h /= 360;
    s /= 100;
    l /= 100;
    
    let r, g, b;
    
    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    
    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    };
  };
  
  const fgHsl = rgbToHsl(fgRgb.r, fgRgb.g, fgRgb.b);
  const bgLuminance = hexToRgb(background);
  const isDarkBg = bgLuminance ? (bgLuminance.r + bgLuminance.g + bgLuminance.b) / 3 < 128 : false;
  
  let adjustedHsl = { ...fgHsl };
  let attempts = 0;
  const maxAttempts = 20;
  
  while (attempts < maxAttempts) {
    const adjustedRgb = hslToRgb(adjustedHsl.h, adjustedHsl.s, adjustedHsl.l);
    const adjustedHex = rgbToHex(adjustedRgb.r, adjustedRgb.g, adjustedRgb.b);
    const contrast = checkContrast(adjustedHex, background);
    
    if (contrast.ratio >= targetRatio) {
      return adjustedHex;
    }
    
    // Adjust lightness based on background
    if (isDarkBg) {
      adjustedHsl.l = Math.min(100, adjustedHsl.l + 5);
    } else {
      adjustedHsl.l = Math.max(0, adjustedHsl.l - 5);
    }
    
    attempts++;
  }
  
  // If we can't meet the target, return the best effort
  return foreground;
}

/**
 * Get suggested colors that meet contrast requirements
 */
export function getSuggestedColors(
  background: string,
  targetRatio: number = 4.5
): string[] {
  const suggestions: string[] = [];
  const baseColors = [
    '#000000', '#FFFFFF', // Black and white
    '#1F2937', '#F9FAFB', // Grays
    '#1E40AF', '#3B82F6', '#60A5FA', // Blues
    '#166534', '#22C55E', '#86EFAC', // Greens
    '#991B1B', '#DC2626', '#F87171', // Reds
    '#92400E', '#D97706', '#FBBF24', // Ambers
  ];
  
  for (const color of baseColors) {
    const contrast = checkContrast(color, background);
    if (contrast.ratio >= targetRatio) {
      suggestions.push(color);
    } else {
      // Try to adjust the color
      const adjusted = adjustColorForContrast(color, background, targetRatio);
      if (adjusted !== color) {
        const adjustedContrast = checkContrast(adjusted, background);
        if (adjustedContrast.ratio >= targetRatio) {
          suggestions.push(adjusted);
        }
      }
    }
  }
  
  // Remove duplicates and limit to 8 suggestions
  return [...new Set(suggestions)].slice(0, 8);
}

/**
 * Get the appropriate focus ring class based on variant
 * @param variant - The variant type for the focus ring
 * @param colors - The theme colors object with classes
 * @returns The appropriate focus ring class
 */
export function getFocusRingClass(
  variant: 'default' | 'primary' | 'danger' | 'success' | 'warning' | 'none',
  colors: { classes: Record<string, string> }
): string {
  const focusRingMap = {
    default: colors.classes.focusRingDefault,
    primary: colors.classes.focusRingPrimary,
    danger: colors.classes.focusRingDanger,
    success: colors.classes.focusRingSuccess,
    warning: colors.classes.focusRingWarning,
    none: colors.classes.focusRingNone,
  };
  
  return focusRingMap[variant] || focusRingMap.default;
}